import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import classNames from "classnames";
import type { MaterialNames } from "@/components/material_symbol_names";

/**
 * A reusable confirmation dialog component for job post actions.
 * Used for actions that require user confirmation and show loading state.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {() => void} props.onClose - Handler for dialog close action
 * @param {string} props.headline - Dialog title text
 * @param {string} props.message - Dialog content message
 * @param {string} props.actionButtonText - Text for the primary action button
 * @param {() => void} props.onAction - Handler for the primary action
 * @param {boolean} props.isLoading - Controls loading state of the action button
 * @param {string} props.actionButtonTestId - Test ID for the action button
 * @param {string} props.actionButtonVariant - Variant for the action button
 * @param {string} props.actionButtonTheme - Theme for the action button
 * @param {boolean} props.actionButtonTrailingIcon - Whether to show a trailing icon for the action button
 * @param {string} props.actionButtonIcon - Icon for the action button
 */

interface QuickActionDialogProps {
  open: boolean;
  onClose: () => void;
  headline: string;
  message: string;
  actionButtonText: string;
  onAction: () => void;
  isLoading: boolean;
  actionButtonTestId: string;
  actionButtonVariant?: "filled" | "outlined" | "text" | "filledTonal";
  actionButtonTheme?: "light" | "dark" | "error" | "info" | "warn" | "pgaAssistant";
  actionButtonTrailingIcon?: boolean;
  actionButtonIcon?: MaterialNames;
}

export default function QuickActionDialog({
  open,
  onClose,
  headline,
  message,
  actionButtonText,
  onAction,
  isLoading,
  actionButtonTestId,
  actionButtonVariant,
  actionButtonTheme,
  actionButtonTrailingIcon,
  actionButtonIcon
}: QuickActionDialogProps) {
  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline={headline}
      className="sm:max-w-[423px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={onAction}
            testId={actionButtonTestId}
            disabled={isLoading}
            icon={isLoading ? "progress_activity" : actionButtonIcon ? actionButtonIcon : undefined}
            iconClassName={classNames(isLoading && "animate-spin w-6 h-6 !block")}
            variant={actionButtonVariant ? actionButtonVariant : "filled"}
            theme={actionButtonTheme ? actionButtonTheme : "light"}
            trailingIcon={actionButtonTrailingIcon ? actionButtonTrailingIcon : false}
          >
            {actionButtonText}
          </Button>
        </>
      }
    >
      <p>{message}</p>
    </Dialog>
  );
}
